import { useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { adminApi, hydrateSessionTokens, setAdminAccessToken, ApiError } from '@cevop/api-client';
import { Button, Card, CevopAppLoader, Field, Input, PasswordInput, ToastProvider } from '@cevop/ui';
import { AppRoutes } from './routes';
import { BrandMark } from './components/BackroomChrome';
import { MfaEnrollQr } from './components/MfaEnrollQr';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30_000 } },
});

type MfaPhase = 'password' | 'enroll' | 'recovery' | 'verify';

function Login({ onAuthenticated }: { onAuthenticated: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [challenge, setChallenge] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [otpauthUrl, setOtpauthUrl] = useState<string | null>(null);
  const [showManualKey, setShowManualKey] = useState(false);
  const [mfaEnrolled, setMfaEnrolled] = useState(false);
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryPhrase, setRecoveryPhrase] = useState<string | null>(null);
  const [recoverySaved, setRecoverySaved] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [oidcUrl, setOidcUrl] = useState<string | null>(null);

  useEffect(() => {
    void adminApi.get<{ url?: string }>('/admin/v1/auth/oidc/start').then((result) => {
      if (result.url) setOidcUrl(result.url);
    }).catch(() => {});
  }, []);

  const phase: MfaPhase = !challenge
    ? 'password'
    : secret
      ? 'enroll'
      : recoveryPhrase
        ? 'recovery'
        : 'verify';

  function displayPhrase(phrase: string): string {
    return phrase.includes(' ') ? phrase : phrase.split('-').join(' ');
  }

  async function submitPassword() {
    setBusy(true);
    setError(null);
    setSecret(null);
    setOtpauthUrl(null);
    setShowManualKey(false);
    setMfaEnrolled(false);
    setUseRecovery(false);
    setRecoveryPhrase(null);
    setRecoverySaved(false);
    setCode('');
    try {
      const result = await adminApi.post<{
        challenge?: { token: string };
        session?: { token: string };
      }>('/admin/v1/auth/login', { email, password });
      if (result.session?.token) {
        setAdminAccessToken(result.session.token);
        onAuthenticated();
        return;
      }
      if (!result.challenge?.token) {
        setError('Could not sign in');
        return;
      }
      setChallenge(result.challenge.token);
      try {
        const enroll = await adminApi.post<{ secret?: string; otpauthUrl?: string }>(
          '/admin/v1/auth/mfa/enroll',
          { token: result.challenge.token },
        );
        if (enroll.secret) {
          setSecret(enroll.secret);
          setOtpauthUrl(enroll.otpauthUrl ?? null);
        }
      } catch (err) {
        if (err instanceof ApiError && (err.code === 'ADMIN_MFA_INVALID' || err.status === 401)) {
          setMfaEnrolled(true);
        } else {
          setError(err instanceof Error ? err.message : 'Could not start authenticator setup');
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not sign in');
    } finally {
      setBusy(false);
    }
  }

  async function beginEnroll(token: string) {
    const enroll = await adminApi.post<{ secret?: string; otpauthUrl?: string }>(
      '/admin/v1/auth/mfa/enroll',
      { token },
    );
    if (enroll.secret) {
      setSecret(enroll.secret);
      setOtpauthUrl(enroll.otpauthUrl ?? null);
      setMfaEnrolled(false);
      setUseRecovery(false);
      setShowManualKey(false);
    }
  }

  async function submitEnrollVerify() {
    if (!challenge || !secret) return;
    setBusy(true);
    setError(null);
    try {
      const confirmed = await adminApi.post<{ recoveryPhrase?: string; recoveryCodes?: string[] }>(
        '/admin/v1/auth/mfa/confirm',
        { token: challenge, code },
      );
      const phrase = confirmed.recoveryPhrase ?? confirmed.recoveryCodes?.[0] ?? null;
      if (!phrase) {
        setError('Recovery phrase was not issued. Contact another Super Admin.');
        return;
      }
      setRecoveryPhrase(displayPhrase(phrase));
      const again = await adminApi.post<{ challenge: { token: string } }>('/admin/v1/auth/login', {
        email,
        password,
      });
      setChallenge(again.challenge.token);
      setSecret(null);
      setOtpauthUrl(null);
      setShowManualKey(false);
      setMfaEnrolled(true);
      setUseRecovery(false);
      setRecoverySaved(false);
      setCode('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid code');
    } finally {
      setBusy(false);
    }
  }

  async function finishAfterRecovery() {
    if (!recoverySaved) {
      setError('Confirm you have stored your recovery phrase somewhere safe before continuing.');
      return;
    }
    setRecoveryPhrase(null);
    setRecoverySaved(false);
    setCode('');
    setError(null);
  }

  async function submitVerify() {
    if (!challenge) return;
    setBusy(true);
    setError(null);
    try {
      if (useRecovery) {
        const recovered = await adminApi.post<{
          challenge?: { token: string };
          requiresMfaEnrollment?: boolean;
          session?: { token: string };
        }>('/admin/v1/auth/mfa/recovery', { token: challenge, code });
        if (recovered.session?.token) {
          // Legacy path should not occur; never accept a full session from recovery.
          setError('Recovery must re-enroll authenticator. Sign in again.');
          return;
        }
        if (!recovered.challenge?.token) {
          setError('Could not start recovery re-enrollment');
          return;
        }
        setChallenge(recovered.challenge.token);
        setCode('');
        setUseRecovery(false);
        await beginEnroll(recovered.challenge.token);
        return;
      }
      const verified = await adminApi.post<{ session: { token: string } }>('/admin/v1/auth/mfa/verify', {
        token: challenge,
        code,
      });
      setAdminAccessToken(verified.session.token);
      onAuthenticated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid code');
    } finally {
      setBusy(false);
    }
  }

  async function copyText(value: string, label: string) {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      setError(`Could not copy ${label}`);
    }
  }

  return (
    <div className="login-root">
      <Card className="login-card" padding="lg">
        <div className="login-brand">
          <BrandMark />
        </div>
        <h1 className="login-title">Sign in</h1>

        {phase === 'password' && (
          <form onSubmit={(e) => { e.preventDefault(); void submitPassword(); }}>
            <Field label="Email">
              <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" autoComplete="email" />
            </Field>
            <Field label="Password">
              <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
            </Field>
            {error && <p className="form-error">{error}</p>}
            <Button type="submit" loading={busy} className="login-submit">
              Continue
            </Button>
            {oidcUrl && (
              <a className="btn btn-ghost login-submit" href={oidcUrl}>
                Continue with SSO
              </a>
            )}
          </form>
        )}

        {phase === 'enroll' && secret && (
          <form onSubmit={(e) => { e.preventDefault(); void submitEnrollVerify(); }}>
            <div className="mfa-setup">
              <p className="mfa-step-label">Step 1 · Set up authenticator</p>
              <p className="mfa-hint">
                Scan this QR code with Google Authenticator, Microsoft Authenticator, 1Password, or another TOTP app.
              </p>
              {otpauthUrl?.startsWith('otpauth://') ? (
                <div className="mfa-qr-wrap">
                  <MfaEnrollQr otpauthUrl={otpauthUrl} />
                </div>
              ) : (
                <p className="form-error">Enrollment QR is unavailable. Use the setup key below.</p>
              )}
              <p className="mfa-hint">
                <button
                  type="button"
                  className="mfa-link-button"
                  onClick={() => setShowManualKey((current) => !current)}
                >
                  {showManualKey ? 'Hide setup key' : "Can't scan the QR code?"}
                </button>
              </p>
              {showManualKey && (
                <div className="mfa-manual">
                  <p className="mfa-label">SETUP KEY</p>
                  <p className="mfa-hint">Enter this key manually in your authenticator app.</p>
                  <code className="mfa-secret mfa-secret--key">{secret}</code>
                  <button
                    type="button"
                    className="btn btn-ghost mfa-copy"
                    onClick={() => void copyText(secret, 'setup key')}
                  >
                    Copy setup key
                  </button>
                </div>
              )}
              <p className="mfa-step-label mfa-step-label--spaced">Step 2 · Verify</p>
              <p className="mfa-hint">Enter the 6-digit code from your authenticator app.</p>
            </div>
            <Field label="Authenticator code">
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
              />
            </Field>
            {error && <p className="form-error">{error}</p>}
            <Button type="submit" loading={busy} className="login-submit" disabled={code.trim().length < 6}>
              Verify
            </Button>
          </form>
        )}

        {phase === 'recovery' && recoveryPhrase && (
          <div className="mfa-setup">
            <p className="mfa-step-label">Step 3 · Save your recovery phrase</p>
            <p className="mfa-label">SAVE YOUR RECOVERY PHRASE</p>
            <p className="mfa-hint">
              Use this phrase if you lose access to your authenticator. It is not your SETUP KEY and not a normal login
              code.
            </p>
            <p className="mfa-hint mfa-hint--warn">
              Store it somewhere safe. It is shown once and cannot be retrieved later. Using it resets MFA and requires
              fresh authenticator enrollment.
            </p>
            <code className="mfa-secret mfa-secret--recovery mfa-secret--phrase">{recoveryPhrase}</code>
            <div className="mfa-recovery-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => void copyText(recoveryPhrase, 'recovery phrase')}
              >
                Copy phrase
              </button>
              <a
                className="btn btn-ghost"
                download="cevop-backroom-recovery-phrase.txt"
                href={`data:text/plain;charset=utf-8,${encodeURIComponent(
                  `cevop Backroom recovery phrase\n\n${recoveryPhrase}\n`,
                )}`}
              >
                Download
              </a>
            </div>
            <label className="mfa-ack">
              <input
                type="checkbox"
                checked={recoverySaved}
                onChange={(e) => {
                  setRecoverySaved(e.target.checked);
                  setError(null);
                }}
              />
              <span>I have stored my recovery phrase somewhere safe</span>
            </label>
            {error && <p className="form-error">{error}</p>}
            <Button
              type="button"
              loading={busy}
              className="login-submit"
              disabled={!recoverySaved}
              onClick={() => void finishAfterRecovery()}
            >
              Finish setup
            </Button>
          </div>
        )}

        {phase === 'verify' && (
          <form onSubmit={(e) => { e.preventDefault(); void submitVerify(); }}>
            <p className="mfa-hint">
              {useRecovery
                ? 'Enter your 12-word recovery phrase. This is an emergency reset — your authenticator will be cleared.'
                : 'Enter the 6-digit code from your authenticator app.'}
            </p>
            <Field label={useRecovery ? 'Recovery phrase' : 'Authenticator code'}>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputMode={useRecovery ? 'text' : 'numeric'}
                autoComplete="one-time-code"
                autoFocus
              />
            </Field>
            {mfaEnrolled && (
              <p className="mfa-hint">
                <button
                  type="button"
                  className="mfa-link-button"
                  onClick={() => {
                    setUseRecovery((current) => !current);
                    setCode('');
                    setError(null);
                  }}
                >
                  {useRecovery ? 'Use authenticator code instead' : 'Use recovery phrase instead'}
                </button>
              </p>
            )}
            {error && <p className="form-error">{error}</p>}
            <Button type="submit" loading={busy} className="login-submit">
              {useRecovery ? 'Recover access' : 'Verify'}
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}

function Root() {
  const [ready, setReady] = useState(false);
  const [authed, setAuthed] = useState(false);

  useEffect(() => {
    void (async () => {
      await hydrateSessionTokens();
      const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      const fromOidc = hash.get('admin_session');
      if (fromOidc) {
        setAdminAccessToken(fromOidc);
        window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}`);
      }
      try {
        await adminApi.get('/admin/v1/auth/me');
        setAuthed(true);
      } catch {
        setAdminAccessToken(null);
      } finally {
        setReady(true);
      }
    })();
  }, []);

  if (!ready) return <CevopAppLoader label="Loading Backroom" />;
  if (!authed) return <Login onAuthenticated={() => setAuthed(true)} />;

  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <Root />
      </ToastProvider>
    </QueryClientProvider>
  );
}
